import React, { useState, useCallback } from 'react';
import { Modal, Input, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { submitWebsiteImportBatch } from 'api/group';
import s from './s.module.less';

type Props = {
    open: boolean;
    onClose: () => void;
    onStartScan: (url: string, batchUid: string) => void;
};

const ImportWebsiteModal: React.FC<Props> = ({ open, onClose, onStartScan }) => {
    const [url, setUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleClose = useCallback(() => {
        setUrl('');
        onClose();
    }, [onClose]);

    const handleNext = useCallback(async () => {
        const websites = url
            .split(',')
            .map((u) => u.trim())
            .filter(Boolean);
        if (websites.length === 0 || submitting) return;
        setSubmitting(true);
        try {
            const res = await submitWebsiteImportBatch(websites);
            const batchUid: string | undefined = res?.data?.data?.batchUid;
            if (!batchUid) return;
            setUrl('');
            onStartScan(websites.join(', '), batchUid);
        } finally {
            setSubmitting(false);
        }
    }, [url, submitting, onStartScan]);

    const canNext = url.trim().length > 0;

    return (
        <Modal
            open={open}
            closable={false}
            footer={null}
            width={810}
            destroyOnClose
            className={s.modal}
            onCancel={handleClose}
            zIndex={99999}
        >
            <div className={s.container}>
                <div className={s.header}>
                    <span className={s.headerTitle}>Import from website</span>
                    <span className={s.closeBtn} onClick={handleClose}>
                        <CloseOutlined />
                    </span>
                </div>
                <div className={s.body}>
                    <div className={s.section}>
                        <h3 className={s.sectionTitle}>Paste a team page or provider profile</h3>
                        <p className={s.sectionDesc}>
                            {`Use one or multiple page that list providers or show a provider's profile — we'll pull names, specialties, and photos automatically.`}
                        </p>
                    </div>
                    <Input
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="e.g. yourpractice.com/team, yourpractice.com/providers/dr-smith"
                        className={s.urlInput}
                        onPressEnter={handleNext}
                    />
                </div>
                <div className={s.footer}>
                    <div className={s.footerBorder} />
                    <div className={s.footerActions}>
                        <Button className={s.backBtn} onClick={handleClose}>
                            Back
                        </Button>
                        <Button
                            type="primary"
                            disabled={!canNext}
                            loading={submitting}
                            className={canNext ? s.primaryBtn : s.disabledBtn}
                            onClick={handleNext}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ImportWebsiteModal;
